import {
  BiometricAuth,
  BiometryError,
  BiometryErrorType,
  type CheckBiometryResult,
  type AuthenticateOptions,
  BiometryType,
} from '@aparajita/capacitor-biometric-auth';
import { Injectable, signal } from '@angular/core';

export type BiometricStatus = {
  isAvailable: boolean;
  isStrongAvailable: boolean;
  biometryType: BiometryType;
  biometryTypes: BiometryType[];
  reason: string;
};

@Injectable({
  providedIn: 'root',
})
export class BiometricAuthService {
  private _lastCheck = signal<CheckBiometryResult | null>(null);
  public readonly lastCheck = this._lastCheck.asReadonly();

  public async check(): Promise<CheckBiometryResult> {
    const result = await BiometricAuth.checkBiometry();
    this._lastCheck.set(result);
    return result;
  }

  public async getStatus(): Promise<BiometricStatus> {
    const result = await this.check();

    return {
      isAvailable: result.isAvailable,
      isStrongAvailable: result.strongBiometryIsAvailable,
      biometryType: result.biometryType,
      biometryTypes: result.biometryTypes,
      reason: result.reason,
    };
  }

  public async isStrongBiometryAvailable(): Promise<boolean> {
    const result = await this.check();
    return result.strongBiometryIsAvailable;
  }

  public async authenticate(options?: AuthenticateOptions): Promise<void> {
    const defaultOptions: AuthenticateOptions = {
      reason: 'Подтвердите вашу личность',
      cancelTitle: 'Отмена',
      allowDeviceCredential: false,
      ...options,
    };

    await BiometricAuth.authenticate(defaultOptions);
  }

  public async onResume(
    cb: (info: CheckBiometryResult) => void,
  ): Promise<void> {
    if (!this._lastCheck()) {
      await this.check();
    }
    await BiometricAuth.addResumeListener(cb);
  }

  public isBiometryLockout(err: unknown): boolean {
    return (
      err instanceof BiometryError &&
      err.code === BiometryErrorType.biometryLockout
    );
  }

  public isUserCancel(err: unknown): boolean {
    return (
      err instanceof BiometryError && err.code === BiometryErrorType.userCancel
    );
  }

  public isBiometryNotAvailable(err: unknown): boolean {
    return (
      err instanceof BiometryError &&
      err.code === BiometryErrorType.biometryNotAvailable
    );
  }

  public isBiometryNotEnrolled(err: unknown): boolean {
    return (
      err instanceof BiometryError &&
      err.code === BiometryErrorType.biometryNotEnrolled
    );
  }

  public getBiometryTypeName(type: BiometryType): string {
    switch (type) {
      case BiometryType.touchId:
        return 'pin.types.touch-id';
      case BiometryType.faceId:
        return 'pin.types.face-id';
      case BiometryType.fingerprintAuthentication:
        return 'pin.types.fingerprint';
      case BiometryType.faceAuthentication:
        return 'pin.types.face';
      case BiometryType.irisAuthentication:
        return 'pin.types.iris';
      default:
        return 'pin.types.default';
    }
  }
}
